(() => {
  'use strict';
  if (document.documentElement.dataset.delifreshReady) return;
  document.documentElement.dataset.delifreshReady = 'true';
  const returnFocus = new WeakMap();
  let activeSpecialConditionTrigger = null;
  let purchasedProductIds;

  function getPurchasedProductIds() {
    if (purchasedProductIds) return purchasedProductIds;
    const source = document.getElementById('DlfPurchasedProductIds');
    if (!source) return new Set();
    try {
      purchasedProductIds = new Set(JSON.parse(source.textContent).map(String));
    } catch (_error) {
      purchasedProductIds = new Set();
    }
    return purchasedProductIds;
  }

  function updatePurchasedProductBadges(scope = document) {
    const purchasedIds = getPurchasedProductIds();
    if (!purchasedIds.size) return;
    const products = scope.matches?.('[data-purchased-product][data-product-id]')
      ? [scope]
      : [...scope.querySelectorAll?.('[data-purchased-product][data-product-id]') || []];
    products.forEach((productElement) => {
      const badge = productElement.querySelector('[data-purchased-product-badge]');
      if (!badge) return;
      const purchased = purchasedIds.has(productElement.dataset.productId);
      badge.hidden = !purchased;
      productElement.classList.toggle('product-card--purchased', purchased);
    });
  }

  function closeSpecialCondition({ restoreFocus = true } = {}) {
    const balloon = document.getElementById('SpecialConditionBalloon');
    if (!balloon || balloon.hidden) return;
    balloon.hidden = true;
    balloon.querySelector('[data-special-condition-balloon-content]')?.replaceChildren();
    activeSpecialConditionTrigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && activeSpecialConditionTrigger?.isConnected)
      activeSpecialConditionTrigger.focus({ preventScroll: true });
    activeSpecialConditionTrigger = null;
  }

  function positionSpecialCondition(trigger, balloon) {
    const gap = 10;
    const edge = 12;
    const triggerRect = trigger.getBoundingClientRect();
    const balloonRect = balloon.getBoundingClientRect();
    const maxLeft = Math.max(edge, window.innerWidth - balloonRect.width - edge);
    const left = Math.min(Math.max(triggerRect.left, edge), maxLeft);
    const roomAbove = triggerRect.top - balloonRect.height - gap;
    const top = roomAbove >= edge
      ? roomAbove
      : Math.min(triggerRect.bottom + gap, window.innerHeight - balloonRect.height - edge);
    balloon.style.left = `${left}px`;
    balloon.style.top = `${Math.max(edge, top)}px`;
  }

  function openSpecialCondition(trigger) {
    const source = trigger
      .closest('.special-condition-trigger-wrap')
      ?.querySelector('[data-special-condition-content]');
    const balloon = document.getElementById('SpecialConditionBalloon');
    const target = balloon?.querySelector('[data-special-condition-balloon-content]');
    if (!(source instanceof HTMLTemplateElement) || !balloon || !target) return;

    if (activeSpecialConditionTrigger === trigger && !balloon.hidden) {
      closeSpecialCondition();
      return;
    }
    closeSpecialCondition({ restoreFocus: false });
    target.replaceChildren(source.content.cloneNode(true));
    activeSpecialConditionTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    balloon.hidden = false;
    requestAnimationFrame(() => {
      positionSpecialCondition(trigger, balloon);
      balloon.focus({ preventScroll: true });
    });
  }

  document.addEventListener('click', (event) => {
    const specialConditionTrigger = event.target.closest('[data-special-condition-trigger]');
    if (specialConditionTrigger) {
      event.preventDefault();
      openSpecialCondition(specialConditionTrigger);
    }
    if (event.target.closest('[data-special-condition-close]')) closeSpecialCondition();
    const open = event.target.closest('[data-open-dialog]');
    if (open) {
      const dialog = document.getElementById(open.dataset.openDialog);
      if (dialog && !dialog.open) {
        returnFocus.set(dialog, open);
        dialog.showModal();
      }
    }
    const close = event.target.closest('[data-close-dialog]');
    if (close) close.closest('dialog')?.close();
    if (event.target instanceof HTMLDialogElement) {
      const rect = event.target.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      )
        event.target.close();
    }
    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      const input = step.closest('.quantity-control')?.querySelector('input');
      if (input && !input.disabled) {
        if (Number(step.dataset.quantityStep) > 0) input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    const scrollButton = event.target.closest('[data-scroll-grid]');
    if (scrollButton) {
      const grid = scrollButton.closest('.section-container')?.querySelector('[data-product-grid]');
      grid?.scrollBy({
        left: grid.clientWidth * Number(scrollButton.dataset.scrollGrid),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }
    document.querySelectorAll('.localization[open], .desktop-nav details[open]').forEach((detail) => {
      if (!detail.contains(event.target)) detail.open = false;
    });
    const balloon = document.getElementById('SpecialConditionBalloon');
    if (
      balloon &&
      !balloon.hidden &&
      !balloon.contains(event.target) &&
      !event.target.closest('[data-special-condition-trigger]')
    )
      closeSpecialCondition({ restoreFocus: false });
  });
  document.addEventListener(
    'close',
    (event) => {
      const trigger = returnFocus.get(event.target);
      if (trigger?.isConnected) trigger.focus();
    },
    true,
  );
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeSpecialCondition();
      document.querySelectorAll('.localization[open], .desktop-nav details[open]').forEach((detail) => {
        detail.open = false;
        detail.querySelector('summary')?.focus();
      });
    }
  });
  window.addEventListener('resize', () => closeSpecialCondition({ restoreFocus: false }));
  window.addEventListener(
    'scroll',
    (event) => {
      const balloon = document.getElementById('SpecialConditionBalloon');
      if (balloon?.contains(event.target)) return;
      closeSpecialCondition({ restoreFocus: false });
    },
    { capture: true, passive: true },
  );
  const loadedScripts = new Set(
    [...document.scripts].filter((script) => script.src).map((script) => script.src),
  );
  function initialize(scope) {
    updatePurchasedProductBadges(scope);
    scope.querySelectorAll('.footer-menu').forEach((el) => {
      el.open = !matchMedia('(max-width: 749px)').matches;
    });
    // Theme Editor can add a section whose component was not present on initial load.
    scope.querySelectorAll('script[src]').forEach((source) => {
      if (loadedScripts.has(source.src)) return;
      loadedScripts.add(source.src);
      const script = document.createElement('script');
      script.src = source.src;
      script.defer = true;
      script.addEventListener('error', () => loadedScripts.delete(source.src), { once: true });
      document.head.append(script);
    });
  }
  initialize(document);
  const purchasedBadgeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) updatePurchasedProductBadges(node);
      });
    });
  });
  purchasedBadgeObserver.observe(document.body, { childList: true, subtree: true });
  const mobile = matchMedia('(max-width: 749px)');
  mobile.addEventListener('change', () => initialize(document));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const parent = event.target.closest('details');
    if (parent) parent.open = true;
    event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
  });
})();
